import { AuthService } from './authService';

/**
 * Microsoft Graph API Service
 */
class GraphService {
    static instance = null;
    static photoCache = new Map(); // Cache for profile photos
    static maxCacheAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    static graphBaseUrl = 'https://graph.microsoft.com/v1.0';

    /**
     * Get singleton instance
     * Ensures single instance of GraphService across application
     */
    static getInstance() {
        if (!GraphService.instance) {
            GraphService.instance = new GraphService();
        }
        return GraphService.instance;
    }

    /**
     * Get user profile information from Microsoft Graph
     * Fetches basic user profile data including displayName, mail, etc.
     * Implements comprehensive error handling and data validation
     */
    static async getUserProfile() {
        try {
            const accessToken = await AuthService.getAccessToken();
            if (!accessToken) {
                throw new Error('No access token available for Graph API call');
            }

            const response = await fetch(`${GraphService.graphBaseUrl}/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Graph API profile request failed: ${response.status} ${response.statusText} - ${errorText}`);
            }

            const profileData = await response.json();
            
            return {
                id: profileData.id,
                displayName: profileData.displayName || profileData.mail?.split('@')[0] || 'User',
                mail: profileData.mail || profileData.userPrincipalName,
                userPrincipalName: profileData.userPrincipalName,
                jobTitle: profileData.jobTitle,
                department: profileData.department,
                officeLocation: profileData.officeLocation,
                mobilePhone: profileData.mobilePhone,
                businessPhones: profileData.businessPhones || []
            };

        } catch (error) {
            throw new Error(`Failed to fetch user profile: ${error.message}`);
        }
    }

    /**
     * Get user profile photo from Microsoft Graph
     * Fetches user's profile photo with multiple size options and caching
     * Implements fallback mechanism when photo is not available
     */
    static async getUserProfilePhoto(userEmail, size = '240x240') {
        try {
            // Check cache first
            const cacheKey = `${userEmail}_${size}`;
            const cachedPhoto = GraphService.getFromCache(cacheKey);
            
            if (cachedPhoto) {
                return cachedPhoto;
            }

            const accessToken = await AuthService.getAccessToken();
            if (!accessToken) {
                return null;
            }

            // Try to get photo metadata first to check if photo exists
            const metadataResponse = await fetch(`${GraphService.graphBaseUrl}/me/photo`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'application/json'
                }
            });

            if (!metadataResponse.ok) {
                if (metadataResponse.status === 404) {
                    GraphService.setInCache(cacheKey, null, 1 * 60 * 60 * 1000); // Cache null for 1 hour
                    return null;
                }
                throw new Error(`Photo metadata request failed: ${metadataResponse.status}`);
            }

            // Get the actual photo data
            const photoResponse = await fetch(`${GraphService.graphBaseUrl}/me/photo/$value`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'image/*'
                }
            });

            if (!photoResponse.ok) {
                if (photoResponse.status === 404) {
                    GraphService.setInCache(cacheKey, null, 1 * 60 * 60 * 1000); // Cache null for 1 hour
                    return null;
                }
                throw new Error(`Photo data request failed: ${photoResponse.status}`);
            }

            // Convert response to blob and then to data URL
            const photoBlob = await photoResponse.blob();
            const photoDataUrl = await GraphService.blobToDataUrl(photoBlob);

            // Cache the photo
            GraphService.setInCache(cacheKey, photoDataUrl);

            return photoDataUrl;

        } catch (error) {
            // Don't throw error, just return null for fallback handling
            return null;
        }
    }

    /**
     * Get user profile photo with multiple size fallbacks
     * Attempts to fetch photo in preferred size with fallbacks to other sizes
     * Ensures best possible photo quality while handling API limitations
     */
    static async getUserProfilePhotoWithFallback(userEmail) {
        // Try different sizes in order of preference
        const sizesToTry = ['240x240', '120x120', '64x64', '48x48'];
        
        for (const size of sizesToTry) {
            try {
                const photo = await GraphService.getUserProfilePhoto(userEmail, size);
                if (photo) {
                    return photo;
                }
            } catch (error) {
                continue;
            }
        }

        return null;
    }

    /**
     * Convert blob to data URL
     * Utility function to convert blob response to base64 data URL
     * Handles different image types and provides proper encoding
     */
    static blobToDataUrl(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = () => {
                resolve(reader.result);
            };
            
            reader.onerror = () => {
                reject(new Error('Failed to convert blob to data URL'));
            };
            
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Cache management - Get from cache
     * Retrieves cached data with expiration check
     * Automatically removes expired cache entries
     */
    static getFromCache(key) {
        const cached = GraphService.photoCache.get(key);
        
        if (!cached) {
            return null;
        }

        // Check if cache entry has expired
        if (Date.now() > cached.expires) {
            GraphService.photoCache.delete(key);
            return null;
        }

        return cached.data;
    }

    /**
     * Cache management - Set in cache
     * Stores data with expiration timestamp
     * Implements automatic cleanup for memory management
     */
    static setInCache(key, data, maxAge = GraphService.maxCacheAge) {
        const expires = Date.now() + maxAge;
        
        GraphService.photoCache.set(key, {
            data,
            expires,
            timestamp: Date.now()
        });

        // Cleanup old cache entries periodically
        GraphService.cleanupCache();
    }

    /**
     * Cache cleanup
     * Removes expired cache entries to prevent memory leaks
     * Runs automatically when new items are added to cache
     */
    static cleanupCache() {
        const now = Date.now();
        
        for (const [key, cached] of GraphService.photoCache.entries()) {
            if (now > cached.expires) {
                GraphService.photoCache.delete(key);
            }
        }
    }

    /**
     * Clear all cached data
     * Utility function to clear photo cache when needed
     * Useful for logout or when user changes profile photo
     */
    static clearCache() {
        GraphService.photoCache.clear();
    }

    /**
     * Get presence information (if needed for future features)
     * Fetches user's presence status from Microsoft Graph
     * Can be used to show online/offline status
     */
    static async getUserPresence() {
        try {
            const accessToken = await AuthService.getAccessToken();
            if (!accessToken) {
                return null;
            }

            const response = await fetch(`${GraphService.graphBaseUrl}/me/presence`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    return null;
                }
                throw new Error(`Presence request failed: ${response.status}`);
            }

            const presenceData = await response.json();
            
            return {
                availability: presenceData.availability,
                activity: presenceData.activity,
                id: presenceData.id
            };

        } catch (error) {
            return null;
        }
    }

    /**
     * Update user profile photo (for future implementation)
     */
    static async updateUserProfilePhoto(photoFile) {
        throw new Error('Profile photo update functionality not implemented');
    }

    /**
     * Get cache statistics
     * Utility function for debugging and monitoring cache performance
     * Returns information about cache size and hit rates
     */
    static getCacheStats() {
        const now = Date.now();
        let totalEntries = 0;
        let expiredEntries = 0;
        let validEntries = 0;

        for (const [key, cached] of GraphService.photoCache.entries()) {
            totalEntries++;
            if (now > cached.expires) {
                expiredEntries++;
            } else {
                validEntries++;
            }
        }

        return {
            totalEntries,
            validEntries,
            expiredEntries,
            cacheSize: GraphService.photoCache.size,
            maxCacheAge: GraphService.maxCacheAge
        };
    }
}

export default GraphService;